// cloudField.ts — The shared cloud COVERAGE field: the flowing 2D bank pattern a
// planet's cloud deck is built from, factored out so more than one shader can sample
// the SAME clouds. Clouds.tsx ray-marches it into a volumetric deck; CloudShadows.tsx
// samples it up-sun to cast the deck's shadow onto the surface. Keeping the flow +
// coverage here (params + GLSL) guarantees the shadows line up with the visible
// clouds. (Clouds.tsx still carries its own march-specific code; this module is the
// common denominator — the per-planet params and the coverage function.)
import { Vector3 } from 'three';

// Standing storm vortices per planet (GLSL array size === JS uniform array length).
export const CLOUD_NVORTEX = 3;

interface Vortex {
  center: [number, number, number];
  data: [number, number, number]; // angular radius, spiral twist, spin rate
}

export interface CloudParams {
  axis: [number, number, number];
  speed: number;
  phase: number;
  baseFlow: number;
  bandCount: number;
  bandPhase: number;
  vortices: Vortex[];
}

// mulberry32 — deterministic PRNG so a planet's seed yields stable flow parameters.
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// A uniform random point on the unit sphere from two [0,1) draws.
function spherePoint(u: number, v: number, zScale = 1): [number, number, number] {
  const theta = 2 * Math.PI * u;
  const z = (2 * v - 1) * zScale;
  const s = Math.sqrt(Math.max(0, 1 - z * z));
  return [s * Math.cos(theta), z, s * Math.sin(theta)];
}

// Per-planet flow field: spin axis, zonal belts, and standing storm vortices — all
// seed-derived so a planet always flows the same way and no two flow alike. (Kept in
// lockstep with Clouds.tsx's cloudParams so the shadows track the visible deck.)
export function cloudParams(seed: number): CloudParams {
  const r = mulberry32(seed);
  const axis = spherePoint(r(), r());
  const speed = (0.006 + r() * 0.014) * (r() < 0.5 ? -1 : 1);
  const phase = r() * 100;
  const baseFlow = 0.3 + r() * 0.5;
  const bandCount = 3 + Math.floor(r() * 4);
  const bandPhase = r() * Math.PI * 2;
  const vortices: Vortex[] = [];
  for (let i = 0; i < CLOUD_NVORTEX; i++) {
    const center = spherePoint(r(), r(), 0.85);
    const r0 = 0.16 + r() * 0.18;
    const twist = (0.5 + r() * 0.9) * (r() < 0.5 ? -1 : 1);
    const spin = (0.01 + r() * 0.025) * (r() < 0.5 ? -1 : 1);
    vortices.push({ center, data: [r0, twist, spin] });
  }
  return { axis, speed, phase, baseFlow, bandCount, bandPhase, vortices };
}

// Global multiplier on each kind's cloud cover, shared by the deck AND its shadows so both
// classify the same fraction of the disc as cloud. Full per-kind coverage rendered the deck as
// a near-solid grey shroud that hid the oceans; pulling it back lets the blue surface show
// through with distinct white swirls (Apollo "Blue Marble"). Applied HERE (once) so CloudShadows
// can't drift out of step with Clouds by scaling differently.
export const CLOUD_COVERAGE_SCALE = 0.5;

// Class cloud cover (0..1) → the coverage threshold band. More cover lowers the band so more of
// the disc is cloud. Scaled by CLOUD_COVERAGE_SCALE so the deck and its shadows agree exactly.
export function cloudThresholds(coverage: number): { coverLo: number; coverHi: number } {
  const cov = Math.max(0, Math.min(1, coverage)) * CLOUD_COVERAGE_SCALE;
  return { coverLo: 0.6 + (-0.04 - 0.6) * cov, coverHi: 0.74 + (0.28 - 0.74) * cov };
}

// Uniforms for the shared field. uTime is advanced each frame by the consumer; the
// rest are seed-stable. `color` is carried for convenience (shadow tint reuse).
export function makeCloudFieldUniforms(seed: number, coverage: number, tidalLock: number) {
  const p = cloudParams(seed);
  const { coverLo, coverHi } = cloudThresholds(coverage);
  return {
    uTime: { value: 0 },
    uFreq: { value: 3.8 },
    uCoverLo: { value: coverLo },
    uCoverHi: { value: coverHi },
    uDriftAxis: { value: new Vector3(...p.axis) },
    uDriftSpeed: { value: p.speed },
    uBaseFlow: { value: p.baseFlow },
    uBandCount: { value: p.bandCount },
    uBandPhase: { value: p.bandPhase },
    uVortexCenter: { value: p.vortices.map((v) => new Vector3(...v.center)) },
    uVortexData: { value: p.vortices.map((v) => new Vector3(...v.data)) },
    uTidalLock: { value: tidalLock },
  };
}

// GLSL: the uniform declarations + the flow warp + a 3-octave fBm + cloudCover(dir),
// which returns 0..1 cloud coverage at an object-space direction. Splice SIMPLEX3D
// (for snoise) BEFORE this. Matches Clouds.tsx's bank coverage (sans the volumetric
// vertical profile + climate gate, which don't affect the shadow footprint).
export const CLOUD_FIELD_PARS = /* glsl */ `
  #define CLOUD_NVORTEX ${CLOUD_NVORTEX}
  uniform float uTime;
  uniform float uFreq;
  uniform float uCoverLo;
  uniform float uCoverHi;
  uniform vec3 uDriftAxis;
  uniform float uDriftSpeed;
  uniform float uBaseFlow;
  uniform float uBandCount;
  uniform float uBandPhase;
  uniform vec3 uVortexCenter[CLOUD_NVORTEX];
  uniform vec3 uVortexData[CLOUD_NVORTEX];
  uniform float uTidalLock;   // 1 = tidally locked: no spin, so no zonal drift / latitude belts

  mat3 cloudRotAxis(vec3 a, float ang){
    float c = cos(ang), s = sin(ang), t = 1.0 - c;
    return mat3(
      t*a.x*a.x + c,     t*a.x*a.y - s*a.z, t*a.x*a.z + s*a.y,
      t*a.x*a.y + s*a.z, t*a.y*a.y + c,     t*a.y*a.z - s*a.x,
      t*a.x*a.z - s*a.y, t*a.y*a.z + s*a.x, t*a.z*a.z + c
    );
  }

  vec3 cloudFlow(vec3 d0){
    vec3 axis = normalize(uDriftAxis);
    // Rotating worlds get rigid zonal drift + Coriolis latitude belts. A tidally-locked world
    // does NOT spin, so it has neither — its circulation is anchored to the fixed substellar
    // point. rot=0 there kills the drift and the belts, leaving the deck stationary (only the
    // slow convective vortex churn) so the substellar weather gate organises it into an eyeball.
    float rot = 1.0 - uTidalLock;
    vec3 dir = cloudRotAxis(axis, uTime * uDriftSpeed * uBaseFlow * rot) * d0;
    float lat = asin(clamp(dot(dir, axis), -1.0, 1.0));
    float band = 0.13 * rot * cos(lat * uBandCount + uBandPhase + uTime * uDriftSpeed * 1.5);
    dir = cloudRotAxis(axis, band) * dir;
    for (int i = 0; i < CLOUD_NVORTEX; i++) {
      vec3 c = uVortexCenter[i];
      float r0 = uVortexData[i].x;
      float g = acos(clamp(dot(d0, c), -1.0, 1.0));
      float wv = exp(-(g * g) / (r0 * r0));
      float ang = uVortexData[i].y * wv * (0.75 + 0.25 * sin(uTime * uVortexData[i].z));
      dir = cloudRotAxis(c, ang) * dir;
    }
    return dir;
  }

  float cloudFbm(vec3 p){
    float s = 0.0, a = 0.5, tot = 0.0;
    for (int i = 0; i < 3; i++) { s += a * snoise(p); p *= 2.03; tot += a; a *= 0.5; }
    return (s / tot) * 0.5 + 0.5;
  }

  // 0..1 cloud coverage at an object-space (deck-local) unit direction.
  float cloudCover(vec3 dir){
    vec3 wdir = cloudFlow(dir);
    float base = cloudFbm(wdir * uFreq);
    float detail = cloudFbm(wdir * uFreq * 2.7 + 19.0);
    return smoothstep(uCoverLo, uCoverHi, base - (1.0 - detail) * 0.22);
  }
`;
